import * as React from 'react'

import { EntitySelect, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { useLocalStorage } from 'usehooks-ts'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { useLocationSelector } from 'modules/shift_management/hooks/useLocationSelector'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import { useSelectableLocations } from 'modules/shift_management/lib/useSelectableLocations'

interface Props {
  onLocationSelect?: (locationId: number) => void
  selectedLocationIds?: number[]
  placeholder?: string
  width?: number
}

const LocationSelector = ({ width = 250 }: Props) => {
  const { selected, updateSelected, applyLocationSelection } = useLocationSelector()
  const { employee } = useCurrentSession()
  const [{ canSeeShiftsForEverybody }] = usePermissions()
  const { loading, locations } = useSelectableLocations(employee?.id, canSeeShiftsForEverybody)
  const [locationIdsStorage, setLocationIdsStorage] = useLocalStorage<string>(
    LOCAL_STORAGE_KEYS.shiftEmployeeLocationIds,
    ''
  )

  const locationEntities = React.useMemo(() => {
    if (loading || locations.length <= 0) return []

    return locations.map((option) => ({
      id: option.id,
      name: option.name,
    }))
  }, [locations.length, loading])

  const getInitialSelectedLocations = (): EntitySelectEntity[] => {
    if (locationIdsStorage.length > 0) {
      const storedLocationIds = JSON.parse(locationIdsStorage)
      return locationEntities.filter((entity) => storedLocationIds.includes(entity.id))
    }

    return locationEntities
  }

  useUnsafeEffect(() => {
    if (loading || locations.length <= 0) return

    const initialSelectedLocations = getInitialSelectedLocations()
    applyLocationSelection(initialSelectedLocations)
  }, [locations.length, loading])

  useUnsafeEffect(() => {
    if (!selected.length) return
    setLocationIdsStorage(
      JSON.stringify(selected.map((location: EntitySelectEntity) => location.id))
    )
  }, [selected, setLocationIdsStorage])

  return (
    <EntitySelect
      hiddenAvatar
      hideLabel
      label={i18n.t('shift_management.workplace_selector.search_placeholder')}
      actions={[
        {
          label: i18n.t('shift_management.workplace_selector.apply_label'),
          onClick: () => {
            applyLocationSelection(selected)
          },
          disabled: selected.length === 0,
        },
      ]}
      clearLabel={i18n.t('shift_management.workplace_selector.clear_label')}
      entities={locationEntities}
      groups={[]}
      notFoundTitle={i18n.t('shift_management.workplace_selector.not_found_title')}
      notFoundSubtitle=""
      onGroupChange={() => {}}
      onItemExpandedChange={() => {}}
      onSelect={(newValue) => {
        updateSelected(newValue)
      }}
      searchPlaceholder={i18n.t('shift_management.workplace_selector.search_placeholder')}
      selectAllLabel={i18n.t('shift_management.workplace_selector.select_all_label')}
      selectedEntities={selected}
      selectedGroup=""
      singleSelector={false}
      selectedItemsCopy={i18n.t('shift_management.workplace_selector.selected_items')}
      width={width}
    />
  )
}

export default LocationSelector
